import { Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AccessControl } from '@pncl/common-models';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged, map, startWith, take } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../environments/environment';
import { Institution, InstitutionTheme } from '../models/user';
import { SpaceRepository } from '../state/space.repository';
import { UIPropsRepository } from '../state/ui-props.repository';
import { ColorUtils } from '../utilities/color.utils';
import { UserService } from './user.service';
import { CapabilityTierService } from './capability-tier.service';
import Capability = AccessControl.Capability;

// Theme name enum for type safety and reduced hardcoded values
export enum ThemeName {
  DEFAULT = 'default',
  SPACEBAR = 'spacebar',
}

export interface ThemeAppData {
  appName: string;
  shortAppName: string;
  searchText: string;
}

const FALLBACK_SQUARE_LOGO = 'assets/icons/icon-180x180.png';

// Theme configuration constants
const DEFAULT_THEME: InstitutionTheme = {
  colors: {
    '--switcher-background': '#ffffff',
    '--sidebar-background': '#ffffff',
    '--sidebar-active-background': '#f0f4ff',
    '--top-bar-background': '#2b2b2b',
    '--gallery-background': '#1a1a1a',
    '--grey-container-background': '#f2f4f7',
    '--gallery-bottom-bar-background': '#1a1a1a',
    '--primary-brand-color': '#2f80ed',
  },
  assets: {
    horizontalLogo: undefined,
    squareLogo: undefined,
    waterMarkLogo: 'assets/icons/pencil-spaces-watermark.png',
    favicon: 'assets/icons/favicon.ico',
  },
};

// Hardcoded theme configurations for global branding
const SPACEBAR_THEME: InstitutionTheme = {
  colors: {
    '--switcher-background': '#ffffff',
    '--sidebar-background': '#ffffff',
    '--sidebar-active-background': '#333333',
    '--top-bar-background': '#2b2b2b',
    '--gallery-background': '#1a1a1a',
    '--grey-container-background': '#f2f4f7',
    '--gallery-bottom-bar-background': '#1a1a1a',
    '--primary-brand-color': '#1F1F1F',
  },
  assets: {
    horizontalLogo: 'assets/svg-icons/spacebar-logo-horizontal.svg',
    waterMarkLogo: 'assets/icons/spacebar-watermark.png',
    squareLogo: 'assets/svg-icons/spacebar-logo.svg',
    favicon: 'assets/icons/spacebar.ico',
  },
};

// Theme map for efficient lookup by name
export const Themes: Record<ThemeName, InstitutionTheme> = {
  [ThemeName.DEFAULT]: DEFAULT_THEME,
  [ThemeName.SPACEBAR]: SPACEBAR_THEME,
};

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private _currentInstitutionTheme = new BehaviorSubject<InstitutionTheme>(DEFAULT_THEME);
  private _horizontalLogo$ = new BehaviorSubject<string | undefined>(undefined);
  private _waterMarkLogo$ = new BehaviorSubject<string | undefined>(undefined);
  private _squareLogo$ = new BehaviorSubject<string | undefined>(undefined);
  private _favicon$ = new BehaviorSubject<string | undefined>(undefined);
  private _currentFaviconPath: string | undefined = undefined;

  readonly squareLogoWithFallback$: Observable<string> = this._squareLogo$.pipe(
    map((logo) => logo || FALLBACK_SQUARE_LOGO),
    distinctUntilChanged(),
  );

  private _updateInstitutionTheme$ = new BehaviorSubject<InstitutionTheme | undefined>(undefined);
  private _savedQueryParamThemeName: ThemeName | null = null;

  constructor(
    private userService: UserService,
    private spaceRepo: SpaceRepository,
    private capabilityTierService: CapabilityTierService,
    private uiPropsRepo: UIPropsRepository,
    private translateService: TranslateService,
  ) {
    this.initializeThemeLogic();
  }

  private initializeThemeLogic(): void {
    // Initialize default theme once at startup (handles query params and stored preferences)
    this.initializeDefaultTheme();

    // Combine the observables to determine the current theme
    combineLatest([
      this.userService.user.pipe(
        map((userData) => ({
          theme: userData?.user?.institution?.theme,
          institution: userData?.user?.institution,
        })),
        startWith({ theme: undefined, institution: undefined }),
      ),
      this.spaceRepo.activeSpace$.pipe(
        map((space) => ({
          theme: space?.institution?.theme,
          institution: space?.institution,
        })),
        startWith({ theme: undefined, institution: undefined }),
      ),
      this._updateInstitutionTheme$.pipe(startWith(undefined)),
      this.capabilityTierService.shouldEnableCapability$(Capability.BRANDING).pipe(
        map((isBrandingEnabled) => ({
          isBrandingEnabled,
        })),
        startWith({ isBrandingEnabled: false }),
      ),
    ])
      .pipe(
        map(([userData, spaceData, externalTheme, isBrandingEnabled]) => {
          // If branding capability is not enabled, always use default theme
          if (!isBrandingEnabled) {
            const defaultTheme = this.getDefaultTheme();
            return {
              theme: defaultTheme,
              fallbackInstitution: undefined,
            };
          }

          // If external theme is provided, merge it with default theme to fill missing values
          if (externalTheme) {
            const defaultTheme = this.getDefaultTheme();
            const mergedTheme = this.mergeThemes(externalTheme, defaultTheme);
            return {
              theme: mergedTheme,
              fallbackInstitution: undefined,
            };
          }

          // Determine the primary theme and fallback institution
          let primaryTheme: InstitutionTheme | undefined;
          let fallbackInstitution: Institution | undefined;

          // If we have a space theme, use it (Inside Spaces, WR/RA)
          if (spaceData.theme) {
            primaryTheme = spaceData.theme;
            fallbackInstitution = spaceData.institution;
          }
          // Otherwise use user theme (Outside Spaces)
          else if (userData.theme) {
            primaryTheme = userData.theme;
            fallbackInstitution = userData.institution;
          }

          // Always merge with the default theme (handles query param theme, local storage theme, and final fallback)
          const defaultTheme = this.getDefaultTheme();
          primaryTheme = this.mergeThemes(primaryTheme, defaultTheme);

          return {
            theme: primaryTheme,
            fallbackInstitution,
          };
        }),
        distinctUntilChanged(),
        untilDestroyed(this),
      )
      .subscribe(({ theme, fallbackInstitution }) => {
        this.applyTheme(theme);
        this._currentInstitutionTheme.next(theme);
        this.updateLogos(theme, fallbackInstitution);
        this.updateFavicon(theme?.assets?.favicon);
        this.updateBranding();
      });
  }

  /**
   * Centralized theme initialization that handles both query params and stored preferences
   * This method runs once at startup and eliminates race conditions
   */
  private initializeDefaultTheme(): void {
    // First, check for query parameter theme
    const urlParams = new URLSearchParams(window.location.search);
    const themeParam = urlParams.get('theme');

    if (themeParam) {
      const themeName = themeParam.toLowerCase() as ThemeName;
      if (themeName in Themes) {
        this._savedQueryParamThemeName = themeName;
        // Wait for UIProps store to be initialized before setting the theme preference
        this.uiPropsRepo.initialized$.pipe(take(1)).subscribe((initialized) => {
          if (initialized) {
            this.uiPropsRepo.setGlobalThemePreference(themeName);
          }
        });
        return;
      }
    }

    // If no query param, check stored preference from UIProps
    this.uiPropsRepo.UIProps$.pipe(take(1)).subscribe((uiProps) => {
      if (uiProps.globalThemePreference && uiProps.globalThemePreference in Themes) {
        this._savedQueryParamThemeName = uiProps.globalThemePreference as ThemeName;
      }
    });
  }

  /**
   * Public method to get the fully resolved theme (merged with default)
   * @param theme - The partial theme to resolve (e.g. from institution)
   * @returns The resolved theme with all properties filled from default if missing
   */
  public getThemeWithDefaults(theme: InstitutionTheme | undefined): InstitutionTheme {
    const defaultTheme = this.getDefaultTheme();
    return this.mergeThemes(theme, defaultTheme);
  }

  /**
   * Gets the default theme based on the initialized theme preference
   * @returns The default theme (query param theme if available, then local storage theme, otherwise DEFAULT theme)
   */
  public getDefaultTheme(): InstitutionTheme {
    // Use the centralized theme preference that was set during initialization
    if (this._savedQueryParamThemeName) {
      return this.getThemeByName(this._savedQueryParamThemeName);
    }

    // A deployment can make another theme its default (app.spacebar.ai → spacebar).
    const envDefault = (environment as unknown as { defaultTheme?: string }).defaultTheme;
    if (envDefault && (Object.values(ThemeName) as string[]).includes(envDefault)) {
      return this.getThemeByName(envDefault as ThemeName);
    }
    return this.getThemeByName(ThemeName.DEFAULT);
  }

  /**
   * Helper method to get theme by name with type safety
   * @param themeName - The theme name to lookup
   * @returns The theme object or default theme if not found
   */
  private getThemeByName(themeName: ThemeName): InstitutionTheme {
    return Themes[themeName] || Themes[ThemeName.DEFAULT];
  }

  /**
   * Determines if a color value should be saved to the institution theme
   * Returns true if the value is non-empty and differs from the default theme
   */
  public shouldSaveColor(formValue: string | undefined, defaultColor: string): boolean {
    return !!(formValue && formValue.trim() !== '' && formValue.trim() !== defaultColor.trim());
  }

  /**
   * Merges two themes, using the fallback theme's properties for any undefined values in the primary theme
   * If primaryTheme is undefined, returns the fallbackTheme
   * @param primaryTheme - The primary theme to use (can be undefined)
   * @param fallbackTheme - The fallback theme to use for undefined properties
   * @returns Merged theme with fallback properties filled in
   */
  private mergeThemes(
    primaryTheme: InstitutionTheme | undefined,
    fallbackTheme: InstitutionTheme,
  ): InstitutionTheme {
    // If no primary theme, return the fallback theme
    if (!primaryTheme) {
      return fallbackTheme;
    }

    // Helper to remove empty/undefined strings from an object
    const removeEmptyValues = (obj: { [key: string]: string | undefined } | undefined) => {
      if (!obj) {
        return {};
      }
      return Object.entries(obj).reduce((acc, [key, value]) => {
        if (typeof value === 'string' && value.trim() !== '') {
          acc[key] = value;
        }
        return acc;
      }, {} as { [key: string]: string });
    };

    return {
      colors: {
        ...fallbackTheme.colors,
        ...removeEmptyValues(primaryTheme.colors),
      },
      assets: {
        ...removeEmptyValues(fallbackTheme.assets),
        ...removeEmptyValues(primaryTheme.assets),
      } as InstitutionTheme['assets'],
    };
  }

  /**
   * Reverts to the normal theme based on current space or user institution
   * This method now clears any external theme override
   */
  public revertToNormalTheme(): void {
    this.updateInstitutionTheme(undefined);
  }

  /**
   * Method to allow outside components to update the theme
   * @param theme - The theme to apply, or undefined to clear external theme override
   */
  public updateInstitutionTheme(theme: InstitutionTheme | undefined): void {
    this._updateInstitutionTheme$.next(theme);
  }

  // Unified logo and favicon management
  private updateLogos(theme: InstitutionTheme, fallbackInstitution?: Institution): void {
    this._horizontalLogo$.next(theme?.assets?.horizontalLogo);
    this._waterMarkLogo$.next(theme?.assets?.waterMarkLogo);
    this._squareLogo$.next(theme?.assets?.squareLogo || fallbackInstitution?.logo);
    this._favicon$.next(theme?.assets?.favicon);

    // Update the favicon in the document head
    this.updateFavicon(theme?.assets?.favicon);
  }

  // Public observables
  get horizontalLogo$(): Observable<string | undefined> {
    return this._horizontalLogo$.asObservable().pipe(distinctUntilChanged());
  }

  get squareLogo$(): Observable<string | undefined> {
    return this._squareLogo$.asObservable().pipe(distinctUntilChanged());
  }

  get favicon$(): Observable<string | undefined> {
    return this._favicon$.asObservable().pipe(distinctUntilChanged());
  }

  get horizontalLogo(): string | undefined {
    return this._horizontalLogo$.value;
  }

  get waterMarkLogo$(): Observable<string | undefined> {
    return this._waterMarkLogo$.asObservable().pipe(distinctUntilChanged());
  }

  get waterMarkLogo(): string | undefined {
    return this._waterMarkLogo$.value;
  }

  get whiteboardWatermarkLogo$(): Observable<string | undefined> {
    return this.waterMarkLogo$.pipe(
      map((logo) => {
        if (logo) {
          return logo;
        }
        const theme = this.currentDefaultTheme;
        if (theme === ThemeName.SPACEBAR) {
          return SPACEBAR_THEME.assets.waterMarkLogo;
        }
        return DEFAULT_THEME.assets.waterMarkLogo;
      }),
    );
  }

  get squareLogo(): string | undefined {
    return this._squareLogo$.value;
  }

  get currentInstitutionTheme$(): Observable<InstitutionTheme> {
    return this._currentInstitutionTheme.asObservable();
  }

  get favicon(): string | undefined {
    return this._favicon$.value;
  }

  get currentDefaultTheme(): ThemeName {
    return this._savedQueryParamThemeName ?? ThemeName.DEFAULT;
  }

  get appName(): string {
    return this.initAppData().appName;
  }

  /** Whether this page is served from spacebar.ai (app.spacebar.ai or any subdomain). */
  private get isSpacebarHost(): boolean {
    const host = window.location.hostname;
    return host === 'spacebar.ai' || host.endsWith('.spacebar.ai');
  }

  /**
   * The product's name, which follows the site's DOMAIN, not the theme: Spacebar on
   * spacebar.ai, Pencil Spaces everywhere else (pencilapp.com, localhost). A theme only
   * changes the look, so a `?theme=` on pencilapp.com no longer renames the product,
   * and app.spacebar.ai is Spacebar whichever theme is active.
   */
  private initAppData(): ThemeAppData {
    switch (this.isSpacebarHost ? ThemeName.SPACEBAR : ThemeName.DEFAULT) {
      case ThemeName.SPACEBAR:
        return {
          appName: 'Spacebar',
          shortAppName: 'Spacebar',
          searchText: `${this.translateService.instant('Search')} Spacebar`,
        };
      case ThemeName.DEFAULT:
      default:
        return {
          appName: 'Pencil Spaces',
          shortAppName: 'Pencil',
          searchText: `${this.translateService.instant('Search')} Pencil Spaces`,
        };
    }
  }

  private applyTheme(theme: InstitutionTheme | undefined): void {
    if (!theme?.colors) {
      return;
    }

    const colorsToApply = Object.keys(theme.colors);

    colorsToApply.forEach((key) => {
      document.documentElement.style.setProperty(key, theme.colors[key]);
    });

    // Generate derived colors for primary button states
    this.generatePrimaryButtonColorVariations(theme);
  }

  /**
   * Generates color variations for primary button states based on the primary color
   * @param theme - The theme containing the primary color
   */
  private generatePrimaryButtonColorVariations(theme: InstitutionTheme): void {
    const primaryColor = theme.colors?.['--primary-brand-color'];

    if (primaryColor) {
      const variations = ColorUtils.generatePrimaryColorVariations(primaryColor);

      // Apply the generated color variations to CSS custom properties
      document.documentElement.style.setProperty(
        '--primary-brand-hover-border-color',
        variations.hoverBorder,
      );
      document.documentElement.style.setProperty(
        '--primary-brand-hover-shadow-color',
        variations.hoverShadow,
      );
      document.documentElement.style.setProperty(
        '--primary-brand-selected-background-color',
        variations.selectedBackground,
      );
    }
  }

  /**
   * Updates the branding of the app (title, meta tags, descriptions, etc.)
   */
  private updateBranding(): void {
    const { appName, shortAppName, searchText } = this.initAppData();

    // Update document title
    document.title = shortAppName;

    // Update meta tags for social media sharing
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', appName);
    }

    // Update search link title
    const searchTitle = document.querySelector('link[title*="Search"]');
    if (searchTitle) {
      searchTitle.setAttribute('title', searchText);
    }
  }

  /**
   * Updates the favicon in the document head
   * @param faviconPath - Path to the favicon file
   */
  private updateFavicon(faviconPath?: string): void {
    // Early return if favicon hasn't changed
    if (this._currentFaviconPath === faviconPath) {
      return;
    }

    this._currentFaviconPath = faviconPath;

    // Remove existing favicon
    const existingFavicon = document.querySelector('link[rel="icon"]');
    existingFavicon?.remove();

    // Add new favicon if path provided
    if (faviconPath) {
      const link = document.createElement('link');
      link.rel = 'icon';
      link.href = faviconPath;
      document.head.appendChild(link);
    }
  }

  /**
   * Manually update the favicon (useful for dynamic favicon changes)
   * @param faviconPath - Path to the favicon file
   */
  public setFavicon(faviconPath: string): void {
    this.updateFavicon(faviconPath);
    this._favicon$.next(faviconPath);
  }
}
